import { capitalize, makeStyles } from '@material-ui/core';
import { FC, useEffect, useState } from 'react';
import Garbage from 'src/asset/garbage.svg';
import CButton from 'src/component/Button';
import Modal from 'src/component/Modal/Modal';
import { FormErrors } from 'src/page/DynamicLinks/interfaces/formErrors.interface';
import AddOrEditAppModal from 'src/page/DynamicLinksConfig/components/AddOrEditAppModal';
import { CreateDynamicLinkRequestBody } from 'src/services/napi/dynamicLinks/createDynamicLink/interfaces/createDynamicLink.interface';
import { DynamicLink } from 'src/services/napi/dynamicLinks/interfaces/dynamicLink.interface';
import { UpdateDynamicLinkRequestBody } from 'src/services/napi/dynamicLinks/updateDynamicLink/interfaces/updateDynamicLink.interface';
import {
  AndroidApp,
  AppleApp
} from 'src/services/napi/projects/interfaces/fetchProjects.interface';
import { isValidUrl } from 'src/util/inputValidation';
import AddOrEditUrlPrefixModal from 'src/page/DynamicLinksConfig/components/AddOrEditUrlPrefixModal';
import { URL_PREFIX_TYPES_TO_DESCRIPTIONS } from 'src/page/DynamicLinksConfig/constants/urlPrefixTypesToDescriptions';
import { UrlPrefixType } from 'src/page/DynamicLinksConfig/interfaces/urlPrefixType.interface';
import ModalInputGroup from 'src/component/ModalInputGroup';
import ModalSelectGroup from 'src/component/ModalSelectGroup';

const useStyle = makeStyles({
  form: {
    '& > :nth-last-child(1)': {
      paddingBottom: '24px'
    },
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  garbage: {
    cursor: 'pointer',
    left: '24px',
    position: 'absolute'
  },
  linkPreview: {
    fontFamily: 'poppins',
    fontSize: '14px',
    lineHeight: '20px',
    marginBottom: '0',
    wordBreak: 'break-all'
  },
  linkPreviewTitle: {
    fontFamily: 'poppins',
    fontSize: '14px',
    lineHeight: '20px',
    margin: '10px 0 0 0'
  },
  sectionTitle: {
    '&:nth-of-type(1)': {
      marginTop: '0px'
    },
    fontFamily: 'poppins',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '22px',
    marginBottom: '16px',
    marginTop: '24px'
  }
});

const DEFAULT_SHORT_LINK_PREFIX_OPTION = 'Select short link URL prefix';
const DEFAULT_DEEP_LINK_PREFIX_OPTION = 'Select deep link URL prefix';
const DEFAULT_FALLBACK_URL_PREFIX_OPTION = 'Select fallback URL prefix';
const DEFAULT_ANDROID_APP_OPTION = 'Open the deep link URL in browser';
const DEFAULT_APPLE_APP_OPTION = 'Open the deep link URL in browser';

interface AddOrEditDynamicLinkModalProps {
  androidApps: AndroidApp[];
  appleApps: AppleApp[];
  deepLinkPrefixes: string[];
  fallbackUrlPrefixes: string[];
  handleClose: () => void;
  handleCreateDynamicLink: (requestBody: CreateDynamicLinkRequestBody) => void;
  handleDeleteDynamicLink: (dynamicLinkId: string) => void;
  handleUpdateApps: (apps: { androidApps: AndroidApp[]; appleApps: AppleApp[] }) => void;
  handleUpdateDeepLinkPrefixes: (deepLinkPrefixes: string[]) => void;
  handleUpdateDynamicLink: (
    dynamicLinkId: string,
    requestBody: UpdateDynamicLinkRequestBody
  ) => void;
  handleUpdateFallbackUrlPrefixes: (shortLinkPrefixes: string[]) => void;
  handleUpdateShortLinkPrefixes: (shortLinkPrefixes: string[]) => void;
  initialDraft?: DynamicLink;
  isOpen: boolean;
  shortLinkPrefixes: string[];
}

const AddOrEditDynamicLinkModal: FC<AddOrEditDynamicLinkModalProps> = ({
  androidApps,
  appleApps,
  deepLinkPrefixes,
  fallbackUrlPrefixes,
  handleClose,
  handleCreateDynamicLink,
  handleDeleteDynamicLink,
  handleUpdateApps,
  handleUpdateDeepLinkPrefixes,
  handleUpdateDynamicLink,
  handleUpdateFallbackUrlPrefixes,
  handleUpdateShortLinkPrefixes,
  initialDraft,
  isOpen,
  shortLinkPrefixes
}) => {
  const classes = useStyle();

  const isEditing = !!initialDraft;

  const [isDeleting, setIsDeleting] = useState(false);

  const [dynamicLinkDraft, setDynamicLinkDraft] = useState<DynamicLink>({
    ...(initialDraft ?? {})
  });

  const [isAddingNewApp, setIsAddingNewApp] = useState(false);

  const [isAddingShortLinkUrlPrefix, setIsAddingShortLinkUrlPrefix] = useState(false);

  const [isAddingDeepLinkUrlPrefix, setIsAddingDeepLinkUrlPrefix] = useState(false);

  const [isAddingFallbackUrlPrefix, setIsAddingFallbackUrlPrefix] = useState(false);

  const [shortLinkPrefix, setShortLinkPrefix] = useState<string>('');

  const [shortLinkPath, setShortLinkPath] = useState<string>('');

  const [deepLinkPrefix, setDeepLinkPrefix] = useState<string>('');

  const [deepLinkPath, setDeepLinkPath] = useState<string>('');

  const [fallbackUrlPrefix, setFallbackUrlPrefix] = useState<string>('');

  const [fallbackUrlPath, setFallbackUrlPath] = useState<string>('');

  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const validateForm = (): FormErrors => {
    const {
      appStoreId,
      bundleId,
      deepLink,
      deepLinkPrefix,
      displayName,
      fallbackUrl,
      fallbackUrlPrefix,
      imageUrl,
      packageName,
      redirectServiceUrl,
      shortLink
    } = dynamicLinkDraft;

    const errors: FormErrors = {};

    if (!displayName?.length) {
      errors.displayName = 'Dynamic Link name is empty';
    }

    if (!shortLinkPath?.length || !redirectServiceUrl?.length || !shortLink?.length) {
      errors.shortLinkPath = 'Short link URL is incomplete';
    } else if (!isValidUrl(redirectServiceUrl) || !isValidUrl(shortLink)) {
      errors.shortLinkPath = 'Invalid short link URL';
    }

    if (!deepLinkPrefix?.length || !deepLinkPath?.length || !deepLink?.length) {
      errors.deepLinkPath = 'Deep link URL is incomplete';
    } else if (!isValidUrl(deepLink)) {
      errors.deepLinkPath = 'Invalid deep link URL';
    }

    if (!fallbackUrlPrefix?.length || !fallbackUrl?.length) {
      errors.fallbackUrlPath = 'Fallback URL is incomplete';
    } else if (!isValidUrl(fallbackUrl)) {
      errors.fallbackUrlPath = 'Invalid fallback URL';
    }

    if (packageName?.length && !androidApps.some((app) => app.packageName === packageName)) {
      errors.androidApp = "Android app with matching package name doesn't exists";
    }

    if (bundleId?.length && !appleApps.some((app) => app.bundleId === bundleId)) {
      errors.appleApp = "Apple app with matching bundle ID doesn't exists";
    }

    if (appStoreId?.length && !appleApps.some((app) => app.appStoreId === appStoreId)) {
      errors.appleApp = "Apple app with matching app store ID doesn't exists";
    }

    if (imageUrl?.length && !isValidUrl(imageUrl)) {
      errors.imageUrl = 'Invalid preview image URL';
    }

    return errors;
  };

  const getDynamicLinkDraftAsRequestBody = ():
    | CreateDynamicLinkRequestBody
    | UpdateDynamicLinkRequestBody => {
    return {
      androidAppName: dynamicLinkDraft.androidAppName ?? '',
      appStoreId: dynamicLinkDraft.appStoreId ?? '',
      appleAppName: dynamicLinkDraft.appleAppName ?? '',
      bundleId: dynamicLinkDraft.bundleId ?? '',
      deepLink: dynamicLinkDraft.deepLink ?? '',
      deepLinkPrefix: dynamicLinkDraft.deepLinkPrefix ?? '',
      description: dynamicLinkDraft.description?.trim() ?? '',
      displayName: dynamicLinkDraft.displayName?.trim() ?? '',
      fallbackUrl: dynamicLinkDraft.fallbackUrl ?? '',
      fallbackUrlPrefix: dynamicLinkDraft.fallbackUrlPrefix ?? '',
      imageUrl: dynamicLinkDraft.imageUrl ?? '',
      packageName: dynamicLinkDraft.packageName ?? '',
      redirectServiceUrl: dynamicLinkDraft.redirectServiceUrl ?? '',
      shortLink: dynamicLinkDraft.shortLink ?? '',
      title: dynamicLinkDraft.title?.trim() ?? ''
    };
  };

  const isInputValueValidUrl = (value: string) => {
    // eslint-disable-next-line no-useless-escape
    return !/[^-a-zA-Z0-9()@:%_\+.~#?&//=]/.test(value);
  };

  const handleShortLinkPathChange = (value: string) => {
    if (isInputValueValidUrl(value)) {
      setShortLinkPath(value);
      setDynamicLinkDraft((prev) => ({
        ...prev,
        shortLink: `${prev.redirectServiceUrl ?? ''}${value.length ? '/' : ''}${value}`
      }));
    }
  };

  const handleDeepLinkPathChange = (value: string) => {
    if (isInputValueValidUrl(value)) {
      setDeepLinkPath(value);
      setDynamicLinkDraft((prev) => ({
        ...prev,
        deepLink: `${prev.deepLinkPrefix ?? ''}${value.length ? '/' : ''}${value}`
      }));
    }
  };

  const handleFallbackUrlPathChange = (value: string) => {
    if (isInputValueValidUrl(value)) {
      setFallbackUrlPath(value);
      setDynamicLinkDraft((prev) => ({
        ...prev,
        fallbackUrl: `${prev.fallbackUrlPrefix ?? ''}${value.length ? '/' : ''}${value}`
      }));
    }
  };

  const handleDisplayNameChange = (value: string) => {
    // eslint-disable-next-line no-useless-escape
    if (!/[^-a-zA-Z0-9()@:%_\+.~#?&//= ]/.test(value)) {
      setDynamicLinkDraft((prev) => ({ ...prev, displayName: value }));
    }
  };

  const handleTitleChange = (value: string) => {
    setDynamicLinkDraft((prev) => ({ ...prev, title: value }));
  };

  const handleImageUrlChange = (value: string) => {
    if (isInputValueValidUrl(value)) {
      setDynamicLinkDraft((prev) => ({ ...prev, imageUrl: value }));
    }
  };

  const handleDescriptionChange = (value: string) => {
    setDynamicLinkDraft((prev) => ({ ...prev, description: value }));
  };

  const handleShortLinkPrefixChange = (value: string) => {
    if (isInputValueValidUrl(value)) {
      setShortLinkPrefix(value);
    }
  };

  const handleDeepLinkPrefixChange = (value: string) => {
    if (isInputValueValidUrl(value)) {
      setDeepLinkPrefix(value);
    }
  };

  const handleFallbackUrlPrefixChange = (value: string) => {
    if (isInputValueValidUrl(value)) {
      setFallbackUrlPrefix(value);
    }
  };

  const handleSelectShortLinkPrefix = (value: string) => {
    if ([dynamicLinkDraft.redirectServiceUrl, DEFAULT_SHORT_LINK_PREFIX_OPTION].includes(value)) {
      return;
    }

    setDynamicLinkDraft((prev) => ({
      ...prev,
      redirectServiceUrl: value,
      shortLink: `${value}${shortLinkPath.length ? '/' : ''}${shortLinkPath}`
    }));
  };

  const handleSelectDeepLinkPrefix = (value: string) => {
    if ([dynamicLinkDraft.deepLinkPrefix, DEFAULT_DEEP_LINK_PREFIX_OPTION].includes(value)) {
      return;
    }

    setDynamicLinkDraft((prev) => ({
      ...prev,
      deepLink: `${value}${deepLinkPath.length ? '/' : ''}${deepLinkPath}`,
      deepLinkPrefix: value
    }));
  };

  const handleSelectFallbackUrlPrefix = (value: string) => {
    if ([dynamicLinkDraft.fallbackUrlPrefix, DEFAULT_FALLBACK_URL_PREFIX_OPTION].includes(value)) {
      return;
    }

    setDynamicLinkDraft((prev) => ({
      ...prev,
      fallbackUrl: `${value}${fallbackUrlPath.length ? '/' : ''}${fallbackUrlPath}`,
      fallbackUrlPrefix: value
    }));
  };

  const handleCloseShortLinkPrefixModal = () => {
    setShortLinkPrefix('');
    setFormErrors((prev) => ({ ...prev, shortLinkPrefix: undefined }));
    setIsAddingShortLinkUrlPrefix(false);
  };

  const handleCloseDeepLinkPrefixModal = () => {
    setDeepLinkPrefix('');
    setFormErrors((prev) => ({ ...prev, deepLinkPrefix: undefined }));
    setIsAddingDeepLinkUrlPrefix(false);
  };

  const handleCloseFallbackUrlPrefixModal = () => {
    setFallbackUrlPrefix('');
    setFormErrors((prev) => ({ ...prev, fallbackUrlPrefix: undefined }));
    setIsAddingFallbackUrlPrefix(false);
  };

  const handleSelectAndroidApp = (value: string) => {
    if (value === dynamicLinkDraft.packageName) {
      return;
    }

    const isDefault = value === DEFAULT_ANDROID_APP_OPTION;
    const packageName = isDefault ? undefined : value;

    const { icon, name } = androidApps.find((app) => app.packageName === packageName) ?? {};

    let { imageUrl, title } = dynamicLinkDraft;

    if (!imageUrl?.length && icon?.length && isValidUrl(icon)) {
      imageUrl = icon;
    }

    if (!title?.length && name?.length) {
      title = name;
    }

    setDynamicLinkDraft((prev) => ({
      ...prev,
      androidAppName: name,
      imageUrl,
      packageName,
      title
    }));
  };

  const handleSelectAppleApp = (value: string) => {
    if (value === dynamicLinkDraft.bundleId) {
      return;
    }

    const isDefault = value === DEFAULT_APPLE_APP_OPTION;
    const bundleId = isDefault ? undefined : value;
    const appStoreId = isDefault
      ? undefined
      : appleApps.find((app) => app.bundleId === value)?.appStoreId ?? undefined;

    const { icon, name } = appleApps.find((app) => app.bundleId === bundleId) ?? {};

    let { imageUrl, title } = dynamicLinkDraft;

    if (!imageUrl?.length && icon?.length && isValidUrl(icon)) {
      imageUrl = icon;
    }

    if (!title?.length && name?.length) {
      title = name;
    }

    setDynamicLinkDraft((prev) => ({
      ...prev,
      appStoreId,
      appleAppName: name,
      bundleId,
      imageUrl,
      title
    }));
  };

  const handleCloseAddAppModal = () => {
    setIsAddingNewApp(false);
  };

  const handleUpdateAppsAndCloseModal = (apps: {
    androidApps: AndroidApp[];
    appleApps: AppleApp[];
  }) => {
    handleUpdateApps(apps);
    handleCloseAddAppModal();
  };

  const handleAddLinkPrefix = ({
    e,
    handleCloseModal,
    handleUpdatePrefixes,
    prefix,
    prefixes,
    setError,
    urlType
  }: {
    e: Event;
    handleCloseModal: () => void;
    handleUpdatePrefixes: (prefixes: string[]) => void;
    prefix: string;
    prefixes: string[];
    setError: (error: string) => void;
    urlType: UrlPrefixType;
  }) => {
    e.preventDefault();

    if (!prefix?.length) {
      setError(`${capitalize(urlType)} URL prefix is empty`);
      return;
    }

    if (!isValidUrl(prefix)) {
      setError(`${capitalize(urlType)} URL prefix is invalid URL`);
      return;
    }

    if (prefixes.includes(prefix)) {
      setError(`${capitalize(urlType)} URL prefix already exists`);
      return;
    }

    handleUpdatePrefixes([...prefixes, prefix]);
    handleCloseModal();
  };

  const onCancel = (e: MouseEvent) => {
    e.preventDefault();
    handleClose();
  };

  const onDeleteDynamicLink = (e: MouseEvent) => {
    e.preventDefault();

    if (!initialDraft?._id) {
      return;
    }

    handleDeleteDynamicLink(initialDraft?._id);
    handleClose();
  };

  const onEditDynamicLink = (e: MouseEvent) => {
    e.preventDefault();

    if (!initialDraft?._id) {
      return;
    }

    const errors = validateForm();
    setFormErrors(errors);

    if (Object.keys(errors).length !== 0) {
      return;
    }

    handleUpdateDynamicLink(initialDraft?._id, getDynamicLinkDraftAsRequestBody());
    handleClose();
  };

  const onCreateDynamicLink = (e: MouseEvent) => {
    e.preventDefault();

    const errors = validateForm();
    setFormErrors(errors);

    if (Object.keys(errors).length !== 0) {
      return;
    }

    handleCreateDynamicLink(getDynamicLinkDraftAsRequestBody());
    handleClose();
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setFormErrors({});
    setIsDeleting(false);
    setIsAddingNewApp(false);
    setIsAddingShortLinkUrlPrefix(false);
    setIsAddingDeepLinkUrlPrefix(false);
    setIsAddingFallbackUrlPrefix(false);
    setDynamicLinkDraft({ ...(initialDraft ?? {}) });

    const initialShortLinkPrefix = initialDraft?.redirectServiceUrl ?? '';
    const initialShortLinkPath =
      initialDraft?.shortLink && initialShortLinkPrefix
        ? initialDraft.shortLink.slice(initialShortLinkPrefix.length + 1)
        : '';

    setShortLinkPrefix(initialShortLinkPrefix);
    setShortLinkPath(initialShortLinkPath);

    const initialDeepLinkPrefix = initialDraft?.deepLinkPrefix ?? '';
    const initialDeepLinkPath =
      initialDraft?.deepLink && initialDeepLinkPrefix
        ? initialDraft.deepLink.slice(initialDeepLinkPrefix.length + 1)
        : '';

    setDeepLinkPrefix(initialDeepLinkPrefix);
    setDeepLinkPath(initialDeepLinkPath);

    const initialFallbackUrlPrefix = initialDraft?.fallbackUrlPrefix ?? '';
    const initialFallbackUrlPath =
      initialDraft?.fallbackUrl && initialFallbackUrlPrefix
        ? initialDraft.fallbackUrl.slice(initialFallbackUrlPrefix.length + 1)
        : '';

    setFallbackUrlPrefix(initialFallbackUrlPrefix);
    setFallbackUrlPath(initialFallbackUrlPath);
  }, [isOpen]);

  if (isDeleting) {
    return (
      <Modal
        footerActions={
          <>
            <CButton
              onClick={() => setIsDeleting(false)}
              type="secondary"
              xstyle={{ height: '40px' }}
            >
              Cancel
            </CButton>

            <CButton onClick={onDeleteDynamicLink} type="primary" xstyle={{ height: '40px' }}>
              Delete
            </CButton>
          </>
        }
        handleClose={() => setIsDeleting(false)}
        isModalOpen={isOpen}
        styles={{
          // @ts-ignore
          '& .MuiDialogContent-root': {
            paddingTop: '0px'
          },
          width: '400px'
        }}
        titleContent={<h3 className="modal-title">Delete Dynamic Link</h3>}
      >
        <h3>
          Are you sure you want to delete this dynamic link{' '}
          <strong>{initialDraft?.displayName ?? ''}</strong> ({initialDraft?.shortLink ?? ''})? You
          can’t undo this action.
        </h3>
      </Modal>
    );
  }

  if (isAddingShortLinkUrlPrefix) {
    return (
      <AddOrEditUrlPrefixModal
        handleClose={handleCloseShortLinkPrefixModal}
        handleCreate={(e: Event) => {
          handleAddLinkPrefix({
            e,
            handleCloseModal: handleCloseShortLinkPrefixModal,
            handleUpdatePrefixes: handleUpdateShortLinkPrefixes,
            prefix: shortLinkPrefix,
            prefixes: shortLinkPrefixes,
            setError: (error: string) =>
              setFormErrors((prev) => ({ ...prev, shortLinkPrefix: error })),
            urlType: 'short link'
          });
        }}
        inputGroupDescription={URL_PREFIX_TYPES_TO_DESCRIPTIONS['short link']}
        inputGroupError={formErrors.shortLinkPrefix}
        inputGroupHtmlFor="standard-input-short-link-url-prefix"
        inputGroupLabel="Short link URL prefix"
        inputGroupOnChange={handleShortLinkPrefixChange}
        inputGroupValue={shortLinkPrefix}
        isOpen={isOpen}
        urlType="short link"
      />
    );
  }

  if (isAddingDeepLinkUrlPrefix) {
    return (
      <AddOrEditUrlPrefixModal
        handleClose={handleCloseDeepLinkPrefixModal}
        handleCreate={(e: Event) => {
          handleAddLinkPrefix({
            e,
            handleCloseModal: handleCloseDeepLinkPrefixModal,
            handleUpdatePrefixes: handleUpdateDeepLinkPrefixes,
            prefix: deepLinkPrefix,
            prefixes: deepLinkPrefixes,
            setError: (error: string) =>
              setFormErrors((prev) => ({ ...prev, deepLinkPrefix: error })),
            urlType: 'deep link'
          });
        }}
        inputGroupDescription={URL_PREFIX_TYPES_TO_DESCRIPTIONS['deep link']}
        inputGroupError={formErrors.deepLinkPrefix}
        inputGroupHtmlFor="standard-input-deep-link-url-prefix"
        inputGroupLabel="Deep link URL prefix"
        inputGroupOnChange={handleDeepLinkPrefixChange}
        inputGroupValue={deepLinkPrefix}
        isOpen={isOpen}
        urlType="deep link"
      />
    );
  }

  if (isAddingFallbackUrlPrefix) {
    return (
      <AddOrEditUrlPrefixModal
        handleClose={handleCloseFallbackUrlPrefixModal}
        handleCreate={(e: Event) => {
          handleAddLinkPrefix({
            e,
            handleCloseModal: handleCloseFallbackUrlPrefixModal,
            handleUpdatePrefixes: handleUpdateFallbackUrlPrefixes,
            prefix: fallbackUrlPrefix,
            prefixes: fallbackUrlPrefixes,
            setError: (error: string) =>
              setFormErrors((prev) => ({ ...prev, fallbackUrlPrefix: error })),
            urlType: 'fallback'
          });
        }}
        inputGroupDescription={URL_PREFIX_TYPES_TO_DESCRIPTIONS.fallback}
        inputGroupError={formErrors.fallbackUrlPrefix}
        inputGroupHtmlFor="standard-input-fallback-url-prefix"
        inputGroupLabel="Fallback URL prefix"
        inputGroupOnChange={handleFallbackUrlPrefixChange}
        inputGroupValue={fallbackUrlPrefix}
        isOpen={isOpen}
        urlType="fallback"
      />
    );
  }

  if (isAddingNewApp) {
    return (
      <AddOrEditAppModal
        androidApps={androidApps}
        appleApps={appleApps}
        handleClose={handleCloseAddAppModal}
        handleUpdateApps={handleUpdateAppsAndCloseModal}
        isOpen={isOpen}
      />
    );
  }

  return (
    <Modal
      footerActions={
        <>
          {isEditing && (
            <img
              alt="delete dynamic link button"
              className={classes.garbage}
              onClick={() => setIsDeleting(true)}
              src={Garbage}
            />
          )}

          <CButton
            htmlType="submit"
            onClick={onCancel}
            type="secondary"
            xstyle={{ height: '40px' }}
          >
            Cancel
          </CButton>

          {isEditing ? (
            <CButton
              htmlType="submit"
              onClick={onEditDynamicLink}
              type="primary"
              xstyle={{ height: '40px' }}
            >
              Edit
            </CButton>
          ) : (
            <CButton
              htmlType="submit"
              onClick={onCreateDynamicLink}
              type="primary"
              xstyle={{ height: '40px' }}
            >
              Create
            </CButton>
          )}
        </>
      }
      handleClose={handleClose}
      isModalOpen={isOpen}
      styles={{
        // @ts-ignore
        '& .MuiDialogContent-root': {
          height: '420px',
          overflowY: 'auto'
        },
        fontSize: '12px',
        width: '560px'
      }}
      titleContent={
        <h4 className="modal-title">{`${isEditing ? 'Edit' : 'Create'} Dynamic Link`}</h4>
      }
    >
      <form className={classes.form} id="new-content-model-form">
        <p className={classes.sectionTitle}>Set up your short URL link</p>

        <ModalSelectGroup
          buttonText="Add new prefix"
          onButtonClick={() => setIsAddingShortLinkUrlPrefix(true)}
          onSelect={handleSelectShortLinkPrefix}
          options={[DEFAULT_SHORT_LINK_PREFIX_OPTION, ...shortLinkPrefixes]}
          value={dynamicLinkDraft.redirectServiceUrl ?? DEFAULT_SHORT_LINK_PREFIX_OPTION}
        />

        <ModalInputGroup
          description="Short link is what users share and click on, so customize your short link URL to make it more professional and contextual."
          error={formErrors.shortLinkPath}
          htmlFor="standard-input-dynamic-link-short-link-path"
          label="Short link URL path (after /)"
          onChange={handleShortLinkPathChange}
          value={shortLinkPath}
        />

        <p className={classes.linkPreviewTitle}>Short link preview:</p>

        <p className={classes.linkPreview}>
          {dynamicLinkDraft.shortLink ? <strong>{dynamicLinkDraft.shortLink}</strong> : '-'}
        </p>

        <p className={classes.sectionTitle}>Set up your deep link</p>

        <ModalSelectGroup
          buttonText="Add new prefix"
          onButtonClick={() => setIsAddingDeepLinkUrlPrefix(true)}
          onSelect={handleSelectDeepLinkPrefix}
          options={[DEFAULT_DEEP_LINK_PREFIX_OPTION, ...deepLinkPrefixes]}
          value={dynamicLinkDraft.deepLinkPrefix ?? DEFAULT_DEEP_LINK_PREFIX_OPTION}
        />

        <ModalInputGroup
          description="The deep link is used to identify correct app to open and pass in data from URL path and query params."
          error={formErrors.deepLinkPath}
          htmlFor="standard-input-dynamic-link-deep-link-path"
          label="Deep link URL path (after /)"
          onChange={handleDeepLinkPathChange}
          value={deepLinkPath}
        />

        <p className={classes.linkPreviewTitle}>Deep link preview:</p>

        <p className={classes.linkPreview}>
          {dynamicLinkDraft.deepLink ? <strong>{dynamicLinkDraft.deepLink}</strong> : '-'}
        </p>

        <p className={classes.sectionTitle}>Set up your fallback URL</p>

        <ModalSelectGroup
          buttonText="Add new prefix"
          onButtonClick={() => setIsAddingFallbackUrlPrefix(true)}
          onSelect={handleSelectFallbackUrlPrefix}
          options={[DEFAULT_FALLBACK_URL_PREFIX_OPTION, ...fallbackUrlPrefixes]}
          value={dynamicLinkDraft.fallbackUrlPrefix ?? DEFAULT_FALLBACK_URL_PREFIX_OPTION}
        />

        <ModalInputGroup
          description="The fallback URL is used on desktop to opens links in web browsers."
          error={formErrors.fallbackUrlPath}
          htmlFor="standard-input-dynamic-link-fallback-url-path"
          label="Fallback URL path (after /)"
          onChange={handleFallbackUrlPathChange}
          value={fallbackUrlPath}
        />

        <p className={classes.linkPreviewTitle}>Fallback URL preview:</p>

        <p className={classes.linkPreview}>
          {dynamicLinkDraft.fallbackUrl ? <strong>{dynamicLinkDraft.fallbackUrl}</strong> : '-'}
        </p>

        <p className={classes.sectionTitle} style={{ marginBottom: '0px' }}>
          Dynamic link name
        </p>

        <ModalInputGroup
          description="The name of this dynamic link (Only for display purposes)."
          error={formErrors.displayName}
          htmlFor="standard-input-dynamic-link-display-name"
          label="Dynamic Link name"
          onChange={handleDisplayNameChange}
          value={dynamicLinkDraft.displayName ?? ''}
        />

        <p className={classes.sectionTitle}>Define link behavior for android</p>

        <ModalSelectGroup
          buttonText="Add new app"
          onButtonClick={() => setIsAddingNewApp(true)}
          onSelect={handleSelectAndroidApp}
          options={[
            DEFAULT_ANDROID_APP_OPTION,
            ...androidApps
              .filter(({ packageName }) => !!packageName)
              .map(({ packageName }) => packageName!)
          ]}
          value={dynamicLinkDraft.packageName ?? DEFAULT_ANDROID_APP_OPTION}
        />

        <p className={classes.sectionTitle}>Define link behavior for apple</p>

        <ModalSelectGroup
          buttonText="Add new app"
          onButtonClick={() => setIsAddingNewApp(true)}
          onSelect={handleSelectAppleApp}
          options={[
            DEFAULT_APPLE_APP_OPTION,
            ...appleApps.filter(({ bundleId }) => !!bundleId).map(({ bundleId }) => bundleId!)
          ]}
          value={dynamicLinkDraft.bundleId ?? DEFAULT_APPLE_APP_OPTION}
        />

        <p className={classes.sectionTitle} style={{ marginBottom: '0px' }}>
          Add social meta tags for better sharing
        </p>

        <ModalInputGroup
          description="The title of the content shared."
          htmlFor="standard-input-dynamic-link-title"
          label="Preview title"
          onChange={handleTitleChange}
          value={dynamicLinkDraft.title ?? ''}
        />

        <ModalInputGroup
          description="The URL for an image that represents the link being shared."
          htmlFor="standard-input-dynamic-link-image-url"
          label="Preview image URL"
          onChange={handleImageUrlChange}
          value={dynamicLinkDraft.imageUrl ?? ''}
        />

        <ModalInputGroup
          description="A brief description of the content shared."
          htmlFor="standard-input-dynamic-link-description"
          label="Preview description"
          onChange={handleDescriptionChange}
          value={dynamicLinkDraft.description ?? ''}
        />
      </form>
    </Modal>
  );
};

export default AddOrEditDynamicLinkModal;
